import React, { useEffect, useState } from 'react';
import { requestJson } from '../../api/http';
import { PRODUCT_AREAS } from '../../api/supportReports';

export const REPORT_STATUSES = ['new', 'triaged', 'fixed', 'closed'] as const;
type Status = (typeof REPORT_STATUSES)[number];

export type ReportRow = {
  org_id: string; receipt_ref: string; product_area: string; status: Status; summary: string; created_at: string;
};
export type ReportDetail = ReportRow & {
  what_happened: string; what_expected: string; route: string; status_note: string;
  activity: Array<{ request_id: string; error_code: string }>;
  has_screenshot: boolean; screenshot?: { type: string; data: string };
};

const BASE = '/v1/admin/support/reports';

export function reportsQuery(filters: { org_id: string; product: string; status: string }): string {
  const params = new URLSearchParams();
  if (filters.org_id.trim()) params.set('org_id', filters.org_id.trim());
  if (filters.product) params.set('product', filters.product);
  if (filters.status) params.set('status', filters.status);
  const text = params.toString();
  return text ? `?${text}` : '';
}

export function ReportsPage() {
  const [filters, setFilters] = useState({ org_id: '', product: '', status: '' });
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [detail, setDetail] = useState<ReportDetail | null>(null);
  const [status, setStatus] = useState<Status>('triaged');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  async function refresh() {
    try {
      const payload = (await requestJson(BASE + reportsQuery(filters))) as { reports: ReportRow[] };
      setRows(payload.reports);
      setError('');
    } catch {
      setError('Could not load the reports.');
    }
  }
  useEffect(() => { void refresh(); }, [filters.org_id, filters.product, filters.status]);

  async function open(ref: string) {
    try {
      const payload = (await requestJson(`${BASE}/${encodeURIComponent(ref)}?screenshot=1`)) as { report: ReportDetail };
      setDetail(payload.report);
      setStatus(payload.report.status);
      setNote(payload.report.status_note || '');
      setError('');
    } catch {
      setError('Could not load that report.');
    }
  }

  async function save() {
    if (!detail) return;
    try {
      await requestJson(`${BASE}/${encodeURIComponent(detail.receipt_ref)}`, {
        method: 'POST', body: JSON.stringify({ status, note }),
      });
      await open(detail.receipt_ref);
      await refresh();
    } catch {
      setError('Could not save the status.');
    }
  }

  return (
    <section>
      <h1>Customer reports</h1>
      {error ? <p role="alert">{error}</p> : null}
      <p>
        <label>Tenant <input aria-label="Filter by tenant" value={filters.org_id}
          onChange={(e) => setFilters({ ...filters, org_id: e.target.value })} /></label>{' '}
        <label>Product <select aria-label="Filter by product" value={filters.product}
          onChange={(e) => setFilters({ ...filters, product: e.target.value })}>
          <option value="">All</option>
          {PRODUCT_AREAS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select></label>{' '}
        <label>Status <select aria-label="Filter by status" value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All</option>
          {REPORT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select></label>
      </p>
      <table>
        <thead><tr><th>Report</th><th>Tenant</th><th>Product</th><th>Status</th><th>Summary</th><th>Received</th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.receipt_ref}>
              <td><button type="button" onClick={() => void open(row.receipt_ref)}>#{row.receipt_ref}</button></td>
              <td>{row.org_id}</td><td>{row.product_area}</td><td>{row.status}</td>
              <td>{row.summary}</td><td>{row.created_at}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {detail ? (
        <article aria-label={`Report ${detail.receipt_ref}`}>
          <h2>Report #{detail.receipt_ref}</h2>
          <p>Tenant {detail.org_id} · {detail.product_area} · {detail.route || 'no route'}</p>
          <h3>What happened</h3><p style={{ whiteSpace: 'pre-wrap' }}>{detail.what_happened}</p>
          <h3>What was expected</h3><p style={{ whiteSpace: 'pre-wrap' }}>{detail.what_expected || '—'}</p>
          {detail.activity.length ? (
            <><h3>Recent activity</h3><ul>{detail.activity.map((a) => (
              <li key={a.request_id}>{a.request_id} · {a.error_code}</li>))}</ul></>
          ) : null}
          {detail.screenshot ? (
            <img alt="Customer screenshot" style={{ maxWidth: '100%' }}
              src={`data:${detail.screenshot.type};base64,${detail.screenshot.data}`} />
          ) : null}
          <p>
            <label>Status <select value={status} onChange={(e) => setStatus(e.target.value as Status)}>
              {REPORT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select></label>
          </p>
          <label>Note shown to the customer <textarea value={note} maxLength={2000}
            onChange={(e) => setNote(e.target.value)} /></label>
          <p><button type="button" onClick={() => void save()}>Save status</button></p>
        </article>
      ) : null}
    </section>
  );
}
